import { BaseFirestore } from "./Firestore";
import { Card } from "./Card";
import { Integrators } from "./Integrator";
import { IconNames, Option } from "./UI";

export const TriggerObjects = {
  card: "card",
} as const;

export type TriggerObject =
  (typeof TriggerObjects)[keyof typeof TriggerObjects];

type TriggerObjectLabelsType = {
  // eslint-disable-next-line no-unused-vars
  [key in TriggerObject]: string;
};

export const TriggerObjectLabels: TriggerObjectLabelsType = {
  [TriggerObjects.card]: "Card",
};

export const TriggerEventTypes = {
  onCreate: "onCreate",
  onUpdate: "onUpdate",
  onDelete: "onDelete",
  // Time-based trigger, evaluated by the scheduled promise-time scanner
  // (functions-v2/src/functions/promiseTime) rather than the reactive
  // onCardWrite path. shouldTrigger intentionally does not handle it.
  onPromiseTime: "onPromiseTime",
  onSchedule: "onSchedule",
} as const;

export type TriggerEventType =
  (typeof TriggerEventTypes)[keyof typeof TriggerEventTypes];

export const TriggerFieldChange = {
  changedTo: "changedTo",
  changedFrom: "changedFrom",
} as const;

export type TriggerFieldChange =
  (typeof TriggerFieldChange)[keyof typeof TriggerFieldChange];

export type TriggerFieldChangeLabelsType = {
  // eslint-disable-next-line no-unused-vars
  [key in TriggerFieldChange]: string;
};

export const TriggerFieldChangeLabels: TriggerFieldChangeLabelsType = {
  [TriggerFieldChange.changedTo]: "changed to",
  [TriggerFieldChange.changedFrom]: "changed from",
};

export type TriggerField =
  | keyof Pick<
      Card,
      | "currentContainer"
      | "color"
      | "technicianId"
      | "isWaiter"
      | "serviceWriterId"
    >
  | "totalLaborHours.estimatedLabor"
  // Synthetic fields (not Card keys) used only to select promise-time behavior
  // on `onPromiseTime` triggers. triggerValue carries lead/grace minutes for
  // approaching/overdue; promiseTimeIsToday and promiseTimeChangedFromToday
  // carry no value.
  | "promiseTimeApproaching"
  | "promiseTimeOverdue"
  | "promiseTimeIsToday"
  | "promiseTimeChangedFromToday"
  | "vehicle.make";

export const TriggerFieldLabels: {
  // eslint-disable-next-line no-unused-vars
  [key in TriggerField]: string;
} = {
  currentContainer: "Current Container",
  color: "Background Color",
  technicianId: "Technician",
  isWaiter: "Is Waiter",
  serviceWriterId: "Service Advisor",
  "totalLaborHours.estimatedLabor": "Total Estimated Labor Hours",
  promiseTimeApproaching: "Promise Time Approaching",
  promiseTimeOverdue: "Promise Time Overdue",
  promiseTimeIsToday: "Promise Time Is Today",
  promiseTimeChangedFromToday: "Promise Time Changed From Today",
  "vehicle.make": "Vehicle Make",
};

export const TriggerConditions = {
  is: "is",
  isNot: "isNot",
  isGreaterThan: "isGreaterThan",
  isLessThan: "isLessThan",
} as const;

export type TriggerCondition =
  (typeof TriggerConditions)[keyof typeof TriggerConditions];

export type TriggerConditionLabelsType = {
  // eslint-disable-next-line no-unused-vars
  [key in TriggerCondition]: string;
};

export const TriggerConditionLabels: TriggerConditionLabelsType = {
  [TriggerConditions.is]: "is",
  [TriggerConditions.isNot]: "is not",
  [TriggerConditions.isGreaterThan]: "is greater than",
  [TriggerConditions.isLessThan]: "is less than",
};

export const ActionObjects = {
  card: "card",
} as const;

export type ActionObject = (typeof ActionObjects)[keyof typeof ActionObjects];

type ActionObjectLabelsType = {
  // eslint-disable-next-line no-unused-vars
  [key in ActionObject]: string;
};

export const ActionObjectLabels: ActionObjectLabelsType = {
  [ActionObjects.card]: "Card",
};

export const ActionEventTypes = {
  update: "update",
  create: "create",
  reset: "reset",
} as const;

export type ActionEventType =
  (typeof ActionEventTypes)[keyof typeof ActionEventTypes];

export type ActionField =
  | keyof Pick<
      Card,
      | "color"
      | "automationAction"
      | "automationActionExpireAt"
      | "automationActionStartAt"
      | "automationActionData"
      | "timer"
    >
  | "stopTimer"
  | "resetTimer"
  | "addTags"
  | "removeTags"
  | "addTagsDelayMinutes"
  | "removeTagsDelayMinutes"
  | "createAndScheduleCustomerUpdate"
  | "createTask"
  | "showToast"
  | "showLabelAsTag"
  | "hideLabelAsTag";

export const ActionFieldLabels: {
  // eslint-disable-next-line no-unused-vars
  [key in ActionField]: string;
} = {
  color: "Background Color",
  automationAction: "Automation Action",
  automationActionExpireAt: "Automation Action Expires At",
  automationActionStartAt: "Automation Action Starts At",
  automationActionData: "Automation Action Data",
  timer: "Timer",
  stopTimer: "Stop Timer",
  resetTimer: "Reset Timer",
  addTags: "Add Tags",
  removeTags: "Remove Tags",
  addTagsDelayMinutes: "Add Tags After Minutes",
  removeTagsDelayMinutes: "Remove Tags After Minutes",
  createAndScheduleCustomerUpdate: "Create and Schedule Customer Update",
  createTask: "Create a Task",
  showToast: "Show Toast Notification",
  showLabelAsTag: "Show Label as Tag",
  hideLabelAsTag: "Hide Label as Tag",
};

export type Trigger = {
  templateId: string;
  triggerObject: TriggerObject;
  triggerEventType: TriggerEventType;
  triggerField: TriggerField | null;
  triggerFieldChange: TriggerFieldChange | null;
  triggerCondition: TriggerCondition | null;
  triggerValue: string | number | boolean | null;
};

export type Action = {
  templateId: string;
  actionObject: ActionObject;
  actionObjectIdentifier: "self" | "new" | string;
  actionEventType: ActionEventType;
  actionField: ActionField | null;
  actionValue: string | number | null;
};

export type Automation = BaseFirestore & {
  id: string;
  shopId: string;
  sortOrder: number;
  triggers: Trigger[];
  triggersConditional: "AND" | "OR";
  actions: Action[];
  name?: string;
  description?: string;
  enabled: boolean;
};

export type CreateAutomationDto = Pick<
  Automation,
  "triggers" | "triggersConditional" | "actions" | "name" | "description"
>;

export const Icons = {
  card: "card",
  dueDate: "dueDate",
  container: "container",
  employee: "employee",
  clock: "clock",
  hourglass: "hourglass",
  customerUpdate: "customerUpdate",
  tags: "tags",
  task: "task",
  zap: "zap",
  vehicle: "vehicle",
  ...Integrators,
  [IconNames.cardCreatedTrigger]: IconNames.cardCreatedTrigger,
  [IconNames.cardMovedIntoContainer]: IconNames.cardMovedIntoContainer,
  [IconNames.cardMovedOutOfContainer]: IconNames.cardMovedOutOfContainer,
  [IconNames.cardChangesToWaiter]: IconNames.cardChangesToWaiter,
  [IconNames.cardChangesToDropOff]: IconNames.cardChangesToDropOff,
  [IconNames.whenPromiseTimeDue]: IconNames.whenPromiseTimeDue,
  [IconNames.whenScheduledTimeOccurs]: IconNames.whenScheduledTimeOccurs,
  [IconNames.isWaiter]: IconNames.isWaiter,
  [IconNames.isDropOff]: IconNames.isDropOff,
  [IconNames.containerNameIs]: IconNames.containerNameIs,
  [IconNames.technicianNameIs]: IconNames.technicianNameIs,
  [IconNames.serviceAdvisorNameIs]: IconNames.serviceAdvisorNameIs,
  [IconNames.estimatedLaborGreater]: IconNames.estimatedLaborGreater,
  [IconNames.estimatedLaborLess]: IconNames.estimatedLaborLess,
  [IconNames.vehicleMakeIs]: IconNames.vehicleMakeIs,
  [IconNames.resetAutomated]: IconNames.resetAutomated,
  [IconNames.wiggleCard]: IconNames.wiggleCard,
  [IconNames.setCardColor]: IconNames.setCardColor,
  [IconNames.wiggleAndSetCardColor]: IconNames.wiggleAndSetCardColor,
  [IconNames.flashColor]: IconNames.flashColor,
  [IconNames.changeCardColor]: IconNames.changeCardColor,
  [IconNames.createCustomerUpdate]: IconNames.createCustomerUpdate,
  [IconNames.createATask]: IconNames.createATask,
  [IconNames.showAToast]: IconNames.showAToast,
  [IconNames.startATimer]: IconNames.startATimer,
  [IconNames.stopATimer]: IconNames.stopATimer,
  [IconNames.resetATimer]: IconNames.resetATimer,
  [IconNames.addTags]: IconNames.addTags,
  [IconNames.removeTags]: IconNames.removeTags,
} as const;

export type BuilderButtonIcon = (typeof Icons)[keyof typeof Icons];

export const DynamicOptions = {
  colors: "colors",
  containers: "containers",
  technicians: "technicians",
  serviceAdvisors: "serviceAdvisors",
  tags: "tags",
  assignees: "assignees",
  vehicleMakes: "vehicleMakes",
} as const;

export type DynamicOptions =
  (typeof DynamicOptions)[keyof typeof DynamicOptions];

export type ButtonOptions = {
  tooltip?: string;
  text: string;
  selectedText?: string;
  icon: BuilderButtonIcon;
};

export type BuilderTrigger = {
  id: string;
  triggerObject: TriggerObject;
  triggerEventType?: TriggerEventType;
  triggerEventTypeOptions?: TriggerEventType[];
  triggerField: TriggerField | null;
  triggerFieldOptions?: TriggerField[] | null;
  triggerFieldChange: TriggerFieldChange | null;
  triggerFieldChangeOptions?: TriggerFieldChange[];
  triggerCondition: TriggerCondition | null;
  triggerConditionOptions?: Option<TriggerCondition>[];
  triggerValueEditable: boolean;
  triggerValue: string | number | boolean | null;
  /**
   * When set, the builder renders a dedicated composite editor for this
   * trigger's value instead of the default Select/Input. The value is stored
   * as a JSON string. Mirrors `multiSelect` on BuilderAction.
   */
  triggerValueEditor?: "schedule";
  triggerValueOptionsDynamic?: DynamicOptions | null;
  triggerValueOptions?: Option[];
  triggerPlaceholder?: string;
  buttonOptions: ButtonOptions;
  conditionalTriggers?: BuilderTrigger[];
};

export type BuilderAction = {
  id: string;
  required?: boolean;
  actionObject: ActionObject;
  actionEventType: ActionEventType;
  actionField: ActionField | null;
  actionFieldOptions?: Option<ActionField>[];
  actionValue: string | null;
  actionValueOptionsDynamic?: DynamicOptions | null;
  actionValueEditable: boolean;
  actionValueOptions?: Option[];
  actionPlaceholder?: string;
  /** When true, the value control is a multi-select and actionValue is a JSON array string. */
  multiSelect?: boolean;
  buttonOptions: ButtonOptions;
  subsequentActions?: BuilderAction[];
};
